import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import {
    Text,
    Avatar,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Spinner,
    TableCellLayout,
    TableColumnDefinition,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    Button,
    Badge,
    Subtitle2,
    tokens
} from '@fluentui/react-components';
import { MessageBarType } from '@fluentui/react';
import {
    Add16Regular,
    EditRegular,
    DeleteRegular,
    DocumentRegular,
    CloudArrowDownRegular,
    MoreHorizontalRegular,
    Document24Regular,
    ChevronDownRegular,
    ChevronUpRegular
} from '@fluentui/react-icons';

// Component functions
import * as ComponentFunctions from './FileUploadList.Functions';

// Models
import { FileUpload } from '../../../../models/Entities/Contracts/FileUpload';
import { FileUploadFilters } from '../../../../models/Filters/FileUploadFilters';

// Components
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import FileUploadFormComponent from '../FileUploadForm/FileUploadForm.Component';
import FilePreview from '../../../Files/FilePreview/FilePreview.Component';
import { PreviewFile, contractFilePreview } from '../../../Files/FilePreview/FilePreview.Functions';
import { triggerDownload } from '../../../Files/FilesExplorer/FilesExplorer.Functions';

// i18n
import { useTranslation } from 'react-i18next';

// Styles
import './FileUploadList.Styles.css';
import { FaFileArchive, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord } from 'react-icons/fa';

interface FileUploadItem extends FileUpload {
    formattedSize: string;
    formattedDate: string;
}

const MAX_VISIBLE_FILES = 3;

const FileUploadListComponent = (props: ComponentFunctions.Props) => {
    
    const { t } = useTranslation();    // State
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(props.viewMode ?? 'grid');
    const [filters, setFilters] = useState<FileUploadFilters>({
        page: 1,
        pageSize: 20,
        searchTerm: '',
        contentType: '',
        entityType: '',
        uploadDateFrom: undefined,
        uploadDateTo: undefined
    });
    const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
    // Vista previa con el mismo drawer del explorador de archivos (PDF inline, Office Online para Word / Excel).
    const [previewFile, setPreviewFile] = useState<PreviewFile | undefined>();
    const [showAllFiles, setShowAllFiles] = useState(false);
    const [animatedTableHeight, setAnimatedTableHeight] = useState<number | null>(null);
    const [isAnimatingTableHeight, setIsAnimatingTableHeight] = useState(false);
    const [pendingToggleHeight, setPendingToggleHeight] = useState<number | null>(null);
    const tableInnerRef = useRef<HTMLDivElement | null>(null);
    // Effects
    useEffect(() => {        // Set entityType from props if available
        const updatedFilters = {
            ...filters,
            entityType: props.entityType ?? filters.entityType,
            entityId: props.ContractId ?? filters.entityId
        };
        props.GetAllFileUploads(updatedFilters);    }, [filters, props.ContractId, props.entityType]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilters(prev => ({ ...prev, page: 1 }));
        }, 500);

        return () => clearTimeout(timeoutId);
    }, []);
    
    // Update view mode when prop changes
    useEffect(() => {
        if (props.viewMode) {
            setViewMode(props.viewMode);
        }
    }, [props.viewMode]);
      // Handlers
    const handleAddFile = () => {
        setIsFormOpen(true);
    };

    const handleDeleteFile = (fileId: number) => {
        if (window.confirm(t('fileUpload.confirmDelete'))) {
            setDeletingFileId(fileId);
            props.DeleteFileUpload?.(fileId);
        }
    };

    // Descarga con el SAS de lectura (sirve para archivos en el storage y para los históricos de la base, que la API
    // copia al storage); si el SAS falla se intenta la descarga directa por la API.
    const handleDownloadFile = (file: FileUpload) => {
        if (!props.RequestContractFileSas) {
            props.DownloadFile?.(file.id);
            return;
        }
        props.RequestContractFileSas(file.id, true)
            .then((token) => triggerDownload(token.sasUrl, file.fileName))
            .catch(() => props.DownloadFile?.(file.id));
    };

    const handleViewFile = (file: FileUpload) => {
        setPreviewFile(contractFilePreview(file));
    };

    const handleEditFile = (file: FileUpload) => {
        if (props.SetEditingFileUpload) {
            props.SetEditingFileUpload(file);
        }
        setIsFormOpen(true);
    };

    const handleFormDismiss = () => {
        setIsFormOpen(false);
        if (props.SetEditingFileUpload) {
            props.SetEditingFileUpload(undefined);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString();
    };    // Transform data for display
    const files = Array.isArray(props.FileUploads) ? props.FileUploads : [];

    useEffect(() => {
        if (files.length <= MAX_VISIBLE_FILES && showAllFiles) {
            setShowAllFiles(false);
        }
    }, [files.length, showAllFiles]);

    const fileItems: FileUploadItem[] = files.map(file => {
        // En caso de que el archivo sea nulo o no tenga las propiedades esperadas
        const safeFile = file || {
            id: 0,
            fileName: 'Unknown file',
            originalFileName: 'Unknown file',
            fileSize: 0,
            contentType: 'unknown',
            uploadDate: new Date(),
            uploadedBy: 'System',
            filePath: '',
            isActive: true,
            File: new File([], 'unknown')
        };

        return {
            ...safeFile,
            formattedSize: formatFileSize(safeFile.fileSize ?? 0),
            formattedDate: formatDate(safeFile.uploadDate?.toString() ?? new Date().toISOString())
        };
    });

    const visibleFileItems = showAllFiles ? fileItems : fileItems.slice(0, MAX_VISIBLE_FILES);
    const hiddenFilesCount = Math.max(fileItems.length - MAX_VISIBLE_FILES, 0);
    const canToggleFileList = hiddenFilesCount > 0;
    const totalFilesCount = props.TotalCount > 0 ? props.TotalCount : fileItems.length;
    const fileCountLabel = totalFilesCount === 1
        ? t('fileUpload.fileCountSingle')
        : t('fileUpload.fileCountPlural', { count: totalFilesCount });

    // Helper to render file icon based on extension o spinner si se está eliminando
    const getFileIcon = (fileExtension?: string, isDeleting?: boolean) => {
        if (isDeleting) return <Spinner size="tiny" />;
        if (!fileExtension) return <Document24Regular />;
        switch (fileExtension.toLowerCase()) {
            case 'pdf':
                return <FaFilePdf style={{ fontSize: '24px' }} />;
            case 'doc':
            case 'docx':
                return <FaFileWord style={{ fontSize: '24px' }} />;
            case 'xls':
            case 'xlsx':
            case 'csv':
                return <FaFileExcel style={{ fontSize: '24px' }} />;
            case 'txt':
                return <FaFileArchive style={{ fontSize: '24px' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return <FaFileImage style={{ fontSize: '24px' }} />;
            case 'zip':
            case 'rar':
                return <FaFileArchive style={{ fontSize: '24px' }} />;
            default:
                return <Document24Regular />;
        }
    };

    // Render list view
    const renderListView = (items: FileUploadItem[]): JSX.Element => {
        return (
            <div className="file-list-view">
                {items.map((item: FileUploadItem) => (
                    <div key={item.id} className="file-list-item" onClick={() => handleViewFile(item)}>
                        <div className="file-list-item-icon">
                            {getFileIcon(item?.fileExtension?.toLocaleLowerCase() ?? '', deletingFileId === item.id && props.isDeletingFile)}
                        </div>
                        <div className="file-list-item-content">
                            <div className="file-list-item-name">{item.fileName}</div>
                            <div className="file-list-item-meta">
                                <span className="file-list-item-meta-info">{item.formattedSize}</span>
                                <span className="file-list-item-meta-info">{item.fileExtension?.toUpperCase() ?? 'FILE'}</span>
                                <span className="file-list-item-meta-info">{item.formattedDate}</span>
                                {item.uploadedBy && (
                                    <span className="file-list-item-meta-info">{item.uploadedBy}</span>
                                )}
                            </div>
                            <div className="file-list-item-actions" onClick={(e) => e.stopPropagation()}>
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<DocumentRegular />}
                                    aria-label={t('fileUpload.view', 'Ver')}
                                    title={t('fileUpload.view', 'Ver')}
                                    onClick={() => handleViewFile(item)}
                                />
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<CloudArrowDownRegular />}
                                    aria-label={t('fileUpload.download')}
                                    title={t('fileUpload.download')}
                                    onClick={() => handleDownloadFile(item)}
                                />
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={deletingFileId === item.id && props.isDeletingFile ? <Spinner size="tiny" /> : <DeleteRegular />}
                                    aria-label={t('fileUpload.delete')}
                                    title={t('fileUpload.delete')}
                                    onClick={() => handleDeleteFile(item.id)}
                                    disabled={deletingFileId === item.id && props.isDeletingFile}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleToggleFileList = () => {
        const currentTableHeight = tableInnerRef.current?.getBoundingClientRect().height;

        if (!currentTableHeight) {
            setShowAllFiles(prev => !prev);
            return;
        }

        setAnimatedTableHeight(currentTableHeight);
        setIsAnimatingTableHeight(true);
        setPendingToggleHeight(currentTableHeight);
        setShowAllFiles(prev => !prev);
    };

    useLayoutEffect(() => {
        if (!isAnimatingTableHeight || pendingToggleHeight === null) {
            return;
        }

        const nextTableHeight = tableInnerRef.current?.scrollHeight;
        if (nextTableHeight && nextTableHeight !== pendingToggleHeight) {
            setAnimatedTableHeight(nextTableHeight);
            setPendingToggleHeight(null);
        }
    }, [showAllFiles, isAnimatingTableHeight, pendingToggleHeight]);

    const handleTableTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.propertyName !== 'height') {
            return;
        }

        setIsAnimatingTableHeight(false);
        setAnimatedTableHeight(null);
        setPendingToggleHeight(null);
    };

    // Table columns
    const columns: TableColumnDefinition<FileUploadItem>[] = [
        createTableColumn<FileUploadItem>({
            columnId: 'name',
            compare: (a, b) => (a.fileName ?? a.fileName).localeCompare(b.fileName ?? b.fileName),
            renderHeaderCell: () => t('fileUpload.fileName'),
            renderCell: (item) => (
                <TableCellLayout media={getFileIcon(item?.fileExtension?.toLocaleLowerCase() ?? '', deletingFileId === item.id && props.isDeletingFile)}>
                    <Text weight="semibold">{item.fileName ?? item.fileName}</Text>
                    {item.description && (
                        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                            {item.description}
                        </Text>
                    )}
                </TableCellLayout>
            ),
        }),
        createTableColumn<FileUploadItem>({
            columnId: 'fileSize',
            compare: (a, b) => (a.fileSize ?? 0) - (b.fileSize ?? 0),
            renderHeaderCell: () => t('fileUpload.size'),
            renderCell: (item) => (
                <TableCellLayout>
                    <Text>{item.formattedSize}</Text>
                </TableCellLayout>
            ),
        }),
        createTableColumn<FileUploadItem>({
            columnId: 'type',
            compare: (a, b) => (a.contentType ?? '').localeCompare(b.contentType ?? ''),
            renderHeaderCell: () => t('fileUpload.type'),
            renderCell: (item) => (
                <TableCellLayout>
                    <Badge appearance="tint">
                        {item.fileExtension?.toUpperCase() ?? item.contentType?.split('/')[1]?.toUpperCase() ?? 'FILE'}
                    </Badge>
                </TableCellLayout>
            ),
        }),
        createTableColumn<FileUploadItem>({
            columnId: 'uploadedBy',
            compare: (a, b) => (a.uploadedBy ?? '').localeCompare(b.uploadedBy ?? ''),
            renderHeaderCell: () => t('fileUpload.uploadedBy'),
            renderCell: (item) => (
                <TableCellLayout media={<Avatar name={item.uploadedBy} size={24} />}>
                    <Text>{item.uploadedBy}</Text>
                </TableCellLayout>
            ),
        }),
        createTableColumn<FileUploadItem>({
            columnId: 'uploadDate',
            compare: (a, b) => new Date(a.uploadDate ?? 0).getTime() - new Date(b.uploadDate ?? 0).getTime(),
            renderHeaderCell: () => t('fileUpload.uploadedAt'),
            renderCell: (item) => (
                <TableCellLayout>
                    <Text>{item.formattedDate}</Text>
                </TableCellLayout>
            ),
        }),
        createTableColumn<FileUploadItem>({
            columnId: 'actions',
            renderHeaderCell: () => t('fileUpload.actions'),
            renderCell: (item) => (
                <TableCellLayout>
                    <Menu>
                        <MenuTrigger>
                            <Button
                                appearance="subtle"
                                icon={<MoreHorizontalRegular />}
                                aria-label={t('fileUpload.moreActions')}
                            />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem
                                    icon={<CloudArrowDownRegular />}
                                    onClick={() => handleDownloadFile(item)}
                                >
                                    {t('fileUpload.download')}
                                </MenuItem>
                                <MenuItem
                                    icon={<DocumentRegular />}
                                    onClick={() => handleViewFile(item)}
                                >
                                    {t('fileUpload.view', 'Ver')}
                                </MenuItem>
                                <MenuItem
                                    icon={<EditRegular />}
                                    onClick={() => handleEditFile(item)}
                                >
                                    {t('fileUpload.edit')}
                                </MenuItem>
                                <MenuItem
                                    icon={deletingFileId === item.id && props.isDeletingFile ? <Spinner size="tiny" /> : <DeleteRegular />}
                                    onClick={() => handleDeleteFile(item.id)}
                                    disabled={deletingFileId === item.id && props.isDeletingFile}
                                >
                                    {t('fileUpload.delete')}
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </TableCellLayout>
            ),
        }),
    ];

    if (props.isLoadingAll) {
        return (
            <div className="file-upload-loading">
                <Spinner size="large" label={t('fileUpload.loading')} />
            </div>
        );
    }

    return (
        <div className="file-upload-list-container">
            <div className="file-upload-list-header">
                <Subtitle2 className="file-upload-list-title">
                    {t('fileUpload.fileUploadsForContract')}
                </Subtitle2>
                <div className="file-upload-files-counter">
                    <Badge appearance="outline" color="brand">
                        {totalFilesCount}
                    </Badge>
                    <Text size={200}>{fileCountLabel}</Text>
                </div>
            </div>

            <div className="file-upload-toolbar">
                <Button
                    appearance="primary"
                    icon={<Add16Regular />}
                    onClick={handleAddFile}
                >
                    {t('fileUpload.uploadFile')}
                </Button>
            </div>

            {props.error && (
                <MessageComponent
                    message={props.error.ErrorMessage || t('fileUpload.errorOccurred')}
                    type={MessageBarType.error}
                />            )}

            <div
                className={`file-upload-table ${isAnimatingTableHeight ? 'is-animating' : ''}`}
                style={animatedTableHeight !== null ? { height: `${animatedTableHeight}px` } : undefined}
                onTransitionEnd={handleTableTransitionEnd}
            >
                <div
                    ref={tableInnerRef}
                    className="file-upload-table-content"
                >
                    {fileItems.length === 0 ? (
                        <div className="empty-state">
                            <DocumentRegular style={{ fontSize: '48px', color: tokens.colorNeutralForeground4 }} />
                            <Text size={400} style={{ color: tokens.colorNeutralForeground2 }}>
                                {t('fileUpload.noFilesUploaded')}
                            </Text>
                            <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                                {t('fileUpload.uploadFirstFile')}
                            </Text>
                            <Button
                                appearance="primary"
                                icon={<Add16Regular />}
                                onClick={handleAddFile}
                                style={{ marginTop: '16px' }}
                            >
                                {t('fileUpload.uploadFile')}
                            </Button>
                        </div>
                    ) : (
                        viewMode === 'grid' ? (
                            <DataGrid
                                items={visibleFileItems}
                                columns={columns}
                                sortable
                                getRowId={(item) => item.id?.toString()}
                                resizableColumns
                            >
                                <DataGridHeader>
                                    <DataGridRow>
                                        {({ renderHeaderCell }) => (
                                            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                                        )}
                                    </DataGridRow>
                                </DataGridHeader>
                                <DataGridBody>
                                    {({ item, rowId }) => (
                                        <DataGridRow key={rowId}>
                                            {({ renderCell }) => (
                                                <DataGridCell>{renderCell(item)}</DataGridCell>
                                            )}
                                        </DataGridRow>
                                    )}
                                </DataGridBody>
                            </DataGrid>
                        ) : (
                            renderListView(visibleFileItems)
                        )
                    )}

                    {canToggleFileList && (
                        <div className="file-upload-toggle">
                            <Button
                                appearance="subtle"
                                icon={showAllFiles ? <ChevronUpRegular /> : <ChevronDownRegular />}
                                onClick={handleToggleFileList}
                            >
                                {showAllFiles
                                    ? t('fileUpload.showLessFiles')
                                    : t('fileUpload.showMoreFiles', { count: hiddenFilesCount })}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <FileUploadFormComponent
                isOpen={isFormOpen}
                onDismiss={handleFormDismiss}
                ContractId={props.ContractId}
            />

            {previewFile && <FilePreview open file={previewFile} onDismiss={() => setPreviewFile(undefined)} />}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(FileUploadListComponent as any);


